import { useEffect, useState } from 'react'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * The people who walked in most recently, sliding past the community section.
 *
 * The section's claim is that the Discord is where everything happens; a live
 * row of names that arrived this week is the cheapest possible proof of it, and
 * a truer one than a member count — a count can be years old, a face that
 * joined yesterday cannot.
 *
 * Name and avatar only, straight from /api/home/recent-joins (which itself
 * carries no IDs). Renders nothing when the list is empty, so a Discord outage
 * costs the section a strip and not a hole.
 */

interface Joiner {
  name: string
  avatar_url: string
}

/** Seconds one card spends crossing the strip. Slow enough to read a name at a
 *  glance, so the row reads as a marquee rather than as a loading spinner. */
const SECONDS_PER_CARD = 3.4

/** The loop is the list rendered twice and translated by exactly half its
 *  width, so it must be long enough that half of it already overflows a wide
 *  viewport — otherwise the seam is visible as a gap. Short lists repeat. */
const MIN_CARDS = 10

export default function RecentJoins() {
  const { T } = useLanguage()
  const [members, setMembers] = useState<Joiner[]>([])

  useEffect(() => {
    let alive = true
    fetch('/api/home/recent-joins')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && Array.isArray(d?.members)) setMembers(d.members) })
      .catch(() => { /* leave the strip out */ })
    return () => { alive = false }
  }, [])

  if (members.length === 0) return null

  const loop: Joiner[] = []
  while (loop.length < MIN_CARDS) loop.push(...members)

  return (
    <div className="mt-10">
      <p className="font-display text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
        {T.home.community.recentTitle}
      </p>

      {/* The mask is what makes it read as motion rather than as a list that
          happens to be cut off: cards arrive out of nothing at both ends. */}
      <div
        className="marquee relative mt-4 overflow-hidden"
        style={{
          maskImage: 'linear-gradient(to right, transparent, black 6%, black 94%, transparent)',
          WebkitMaskImage: 'linear-gradient(to right, transparent, black 6%, black 94%, transparent)',
        }}
      >
        {/* One transform on one element for the whole row — the cards
            themselves never move relative to each other, so the browser can
            keep the strip on its own layer. The animation itself (and the
            hover pause, and the reduced-motion fallback) lives in index.css
            under .marquee / .marquee-track; only the duration is per instance,
            since it depends on how many cards there are. */}
        <div
          className="marquee-track flex w-max"
          style={{ animationDuration: `${loop.length * SECONDS_PER_CARD}s` }}
        >
          {[...loop, ...loop].map((m, i) => (
            <div
              key={i}
              /* The second half is the first half again: it exists to cover the
                 wrap, and a screen reader should hear the names once. */
              aria-hidden={i >= loop.length}
              className="mr-2.5 flex shrink-0 items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] py-1.5 pl-1.5 pr-4"
            >
              <img src={m.avatar_url} alt="" loading="lazy" className="h-8 w-8 rounded-full object-cover" />
              <span className="max-w-[11rem] truncate text-[13px] text-foreground/85">{m.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
